import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  GraduationCap, School, Plus, Search, MoreHorizontal, 
  ArrowLeft, Loader2, AlertCircle, Pencil, Trash2
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

import { API_URL } from '../utils/api';

export function SchoolsPage() {
  const { user } = useAuth();
  const [schools, setSchools] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', organization_id: '', address: '', phone: '', principal_name: '' });
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [schoolsRes, orgsRes] = await Promise.all([
        axios.get(`${API_URL}/api/schools`, { withCredentials: true }),
        axios.get(`${API_URL}/api/organizations`, { withCredentials: true })
      ]);
      setSchools(schoolsRes.data);
      setOrganizations(orgsRes.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.organization_id) {
      setError('Please select an organization');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/schools`, formData, { withCredentials: true });
      setCreateOpen(false);
      setFormData({ name: '', organization_id: '', address: '', phone: '', principal_name: '' });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create school');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (schoolId) => {
    if (!window.confirm('Are you sure you want to delete this school?')) return;
    try {
      await axios.delete(`${API_URL}/api/schools/${schoolId}`, { withCredentials: true });
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete school');
    }
  };

  const getOrgName = (orgId) => organizations.find(o => o.org_id === orgId)?.name || 'Unknown';

  const filteredSchools = schools.filter(s =>
    s.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canManage = ['admin', 'case_manager'].includes(user?.role);

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E5E5]">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-[#F3F4F1] rounded-lg" data-testid="back-btn">
              <ArrowLeft className="h-5 w-5 text-[#595959]" />
            </Link>
            <Link to="/" className="flex items-center gap-2">
              <GraduationCap className="h-7 w-7 text-[#2C4C3B]" />
              <span className="font-outfit text-xl font-semibold text-[#1C1C1C]">FrameEd</span>
            </Link>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-[#1C1C1C] mb-1">Schools</h1>
            <p className="text-[#595959]">Manage schools within your organizations</p>
          </div>
          {canManage && organizations.length > 0 && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#2C4C3B] hover:bg-[#1E3629] text-white rounded-xl" data-testid="create-school-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add School
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-outfit">Create School</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="School name"
                      className="rounded-xl"
                      required
                      data-testid="school-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Organization *</Label>
                    <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})}>
                      <SelectTrigger className="rounded-xl" data-testid="school-org-select">
                        <SelectValue placeholder="Select organization" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.org_id} value={org.org_id}>{org.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Principal Name</Label>
                    <Input
                      value={formData.principal_name}
                      onChange={(e) => setFormData({...formData, principal_name: e.target.value})}
                      placeholder="Dr. Jane Smith"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="(555) 123-4567"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Address</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      placeholder="123 School St"
                      className="rounded-xl"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="rounded-xl">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={submitting} className="bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl" data-testid="submit-school-btn">
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Create
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {error && (
          <div className="flex items-center gap-2 p-4 mb-6 bg-red-50 border border-red-200 rounded-xl text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto">×</button>
          </div>
        )}

        {organizations.length === 0 && !loading && (
          <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-2xl mb-6">
            <p className="text-[#595959]">Create an organization first before adding schools</p>
            <Link to="/organizations">
              <Button className="mt-4 bg-[#2C4C3B] hover:bg-[#1E3629] rounded-xl">Go to Organizations</Button>
            </Link>
          </div>
        )}

        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#595959]" />
          <Input
            placeholder="Search schools..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl bg-white"
            data-testid="search-schools-input"
          />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#2C4C3B]" />
          </div>
        ) : filteredSchools.length === 0 ? (
          <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-2xl">
            <School className="h-12 w-12 mx-auto text-[#595959] mb-4" />
            <h3 className="font-outfit text-lg font-medium text-[#1C1C1C] mb-2">No schools yet</h3>
            <p className="text-[#595959]">Add schools to your organizations</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredSchools.map((school) => (
              <div
                key={school.school_id}
                className="bg-white border border-[#E5E5E5] rounded-2xl p-6 hover:shadow-lg transition-shadow"
                data-testid={`school-card-${school.school_id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#A7C7E7]/20 flex items-center justify-center">
                      <School className="h-6 w-6 text-[#3B6A9B]" />
                    </div>
                    <div>
                      <h3 className="font-outfit text-lg font-semibold text-[#1C1C1C]">{school.name}</h3>
                      <p className="text-sm text-[#595959]">{getOrgName(school.organization_id)}</p>
                      {school.principal_name && <p className="text-sm text-[#595959] mt-1">Principal: {school.principal_name}</p>}
                      {school.address && <p className="text-sm text-[#595959]">{school.address}</p>}
                    </div>
                  </div>
                  {user?.role === 'admin' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(school.school_id)} className="text-red-600">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
